// ============================================================================
// Shells — persistent terminal sessions via xterm.js + node-pty
// ============================================================================
// Depends on: Terminal, FitAddon, WebglAddon (xterm CDN globals),
// escapeHtml (markdown.js), haptic (shared.js global),
// DOM elements (dom.js), wireModal/openModal/closeModal (modal.js).

import {
  sbShellsBtn, sbShellsBadge, terminalOverlay, terminalTabs,
  terminalNew, terminalClose, terminalContainer,
} from "./dom.js";
import { escapeHtml } from "./markdown.js";
import { wireModal, openModal, closeModal, registerEscModal } from "./modal.js";

const shells = new Map(); // id -> { term, fitAddon, pane, tabEl, name, running }
let activeShellId = null;
let wsRef = null; // set by initShells
let activeCwdRef = () => ""; // set by initShells
let connectRef = null; // set by initShells

function sendShell(msg) {
  const ws = wsRef ? wsRef() : null;
  if (ws && ws.readyState === ws.OPEN) {
    ws.send(JSON.stringify(msg));
    return true;
  }
  return false;
}

export function openTerminalModal() {
  openModal(terminalOverlay);
  if (shells.size === 0) {
    createNewShell();
  } else {
    const firstId = shells.keys().next().value;
    switchShellTab(firstId);
  }
  setTimeout(() => {
    const s = shells.get(activeShellId);
    if (s) { try { s.fitAddon.fit(); } catch {} s.term.focus(); }
  }, 50);
}

export function closeTerminalModal() {
  closeModal(terminalOverlay);
}

function createNewShell() {
  const cwd = activeCwdRef() || "";
  sendShell({ type: "shell_create", cwd, cols: 80, rows: 24 });
}

export function onShellCreated(msg) {
  const id = msg.id;
  const name = msg.name;

  const term = new Terminal({
    fontSize: 13,
    fontFamily: "'Cascadia Code', 'Fira Code', 'JetBrains Mono', monospace",
    theme: {
      background: "#0d1117",
      foreground: "#c9d1d9",
      cursor: "#58a6ff",
      selectionBackground: "#264f78",
    },
    cursorBlink: true,
    scrollback: 3000,
    scrollSensitivity: 10,
    fastScrollSensitivity: 50,
    fastScrollGap: 5,
    allowProposedApi: true,
  });

  const fitAddon = new FitAddon.FitAddon();
  term.loadAddon(fitAddon);

  try {
    const webglAddon = new WebglAddon.WebglAddon();
    webglAddon.onContextLoss(() => { webglAddon.dispose(); });
    term.loadAddon(webglAddon);
  } catch { /* WebGL not available */ }

  const pane = document.createElement("div");
  pane.className = "terminal-pane";
  terminalContainer.appendChild(pane);
  term.open(pane);

  // ---- Mobile / touch scrolling: proportional (relative) scroll ----
  const touchState = { raf: null };
  let touchStartY = 0;
  let touchLastY = 0;
  let touchLastTime = 0;
  let touchVelocity = 0;
  function getTermHeight() { return term.element?.clientHeight || pane.clientHeight || 400; }
  function getBufferLength() {
    try { return Math.max(term.rows, term.buffer?.length || term.rows); }
    catch { return term.rows; }
  }
  function scrollByPixels(dy, multiplier = 1) {
    const termHeight = getTermHeight();
    if (termHeight <= 0) return;
    const total = getBufferLength();
    const fraction = (dy / termHeight) * total * multiplier;
    const lines = Math.round(fraction);
    if (lines !== 0) term.scrollLines(lines);
  }
  pane.addEventListener("touchstart", (e) => {
    if (e.touches.length !== 1) return;
    if (touchState.raf) { cancelAnimationFrame(touchState.raf); touchState.raf = null; }
    const t = e.touches[0];
    touchStartY = t.clientY;
    touchLastY = touchStartY;
    touchLastTime = performance.now();
    touchVelocity = 0;
  }, { passive: false });
  pane.addEventListener("touchmove", (e) => {
    if (e.touches.length !== 1) return;
    e.preventDefault();
    e.stopPropagation();
    const t = e.touches[0];
    const now = performance.now();
    const dy = touchLastY - t.clientY;
    const dt = now - touchLastTime;
    scrollByPixels(dy);
    if (dt > 0) touchVelocity = dy / dt;
    touchLastY = t.clientY;
    touchLastTime = now;
  }, { passive: false, capture: true });
  pane.addEventListener("touchend", () => {
    if (Math.abs(touchVelocity) < 0.2) { touchVelocity = 0; return; }
    let v = touchVelocity;
    const decay = 0.92;
    const step = () => {
      if (Math.abs(v) < 0.05) { touchState.raf = null; return; }
      scrollByPixels(v * 16, 2.0);
      v *= decay;
      touchState.raf = requestAnimationFrame(step);
    };
    touchState.raf = requestAnimationFrame(step);
  });

  const tabEl = document.createElement("div");
  tabEl.className = "terminal-tab active";
  tabEl.innerHTML = `<span class="term-dot"></span><span class="term-name">${escapeHtml(name)}</span><span class="term-close">&times;</span>`;
  tabEl.addEventListener("click", (e) => {
    if (e.target.classList.contains("term-close")) {
      e.stopPropagation();
      haptic("press");
      killShell(id);
    } else {
      haptic("tap");
      switchShellTab(id);
    }
  });
  terminalTabs.appendChild(tabEl);

  term.onData((data) => {
    const bytes = new TextEncoder().encode(data);
    let binary = "";
    for (const b of bytes) binary += String.fromCharCode(b);
    sendShell({ type: "shell_input", id, data: btoa(binary) });
  });

  term.onResize(({ cols, rows }) => {
    sendShell({ type: "shell_resize", id, cols, rows });
  });

  let resizeTimer = null;
  const ro = new ResizeObserver(() => {
    if (resizeTimer) clearTimeout(resizeTimer);
    resizeTimer = setTimeout(() => {
      resizeTimer = null;
      try { fitAddon.fit(); } catch {}
    }, 100);
  });
  ro.observe(pane);

  shells.set(id, { term, fitAddon, pane, tabEl, name, running: true, ro, writeBuffer: [], writeRaf: null, touchState });
  switchShellTab(id);
  updateShellsBadge();
  setTimeout(() => { try { fitAddon.fit(); } catch {} term.focus(); }, 50);
}

export function onShellOutput(msg) {
  const s = shells.get(msg.id);
  if (!s) return;
  try {
    const binary = atob(msg.data);
    const bytes = new Uint8Array(binary.length);
    for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
    s.writeBuffer.push(bytes);
    if (!s.writeRaf) {
      s.writeRaf = requestAnimationFrame(() => {
        s.writeRaf = null;
        const buf = s.writeBuffer;
        s.writeBuffer = [];
        for (const chunk of buf) s.term.write(chunk);
      });
    }
  } catch {}
}

export function onShellExit(msg) {
  const s = shells.get(msg.id);
  if (!s) return;
  s.running = false;
  s.term.write(`\r\n\x1b[31m[Process exited with code ${msg.code}]\x1b[0m\r\n`);
  const dot = s.tabEl.querySelector(".term-dot");
  if (dot) dot.classList.add("dead");
}

export function onShellList(msg) {
  const count = msg.shells.filter(s => s.running).length;
  if (sbShellsBadge) {
    if (count > 0) {
      sbShellsBadge.textContent = count;
      sbShellsBadge.classList.remove("hidden");
    } else {
      sbShellsBadge.classList.add("hidden");
    }
  }
  for (const s of msg.shells) {
    if (s.running && !shells.has(s.id)) {
      sendShell({ type: "shell_attach", id: s.id });
    }
  }
}

function switchShellTab(id) {
  for (const [sid, s] of shells) {
    s.pane.classList.remove("active");
    s.tabEl.classList.remove("active");
  }
  const s = shells.get(id);
  if (!s) return;
  s.pane.classList.add("active");
  s.tabEl.classList.add("active");
  activeShellId = id;
  setTimeout(() => { try { s.fitAddon.fit(); } catch {} s.term.focus(); }, 50);
}

function killShell(id) {
  const s = shells.get(id);
  if (!s) return;
  sendShell({ type: "shell_kill", id });
  if (s.writeRaf) cancelAnimationFrame(s.writeRaf);
  if (s.touchState?.raf) cancelAnimationFrame(s.touchState.raf);
  s.term.dispose();
  s.pane.remove();
  s.tabEl.remove();
  s.ro.disconnect();
  shells.delete(id);
  if (activeShellId === id) {
    if (shells.size > 0) {
      switchShellTab(shells.keys().next().value);
    } else {
      activeShellId = null;
      const empty = document.createElement("div");
      empty.className = "terminal-pane empty-msg active";
      empty.textContent = "No terminal sessions. Click \"+ New\" to start one.";
      terminalContainer.appendChild(empty);
    }
  }
  updateShellsBadge();
}

function updateShellsBadge() {
  if (!sbShellsBadge) return;
  const count = shells.size;
  if (count > 0) {
    sbShellsBadge.textContent = count;
    sbShellsBadge.classList.remove("hidden");
  } else {
    sbShellsBadge.classList.add("hidden");
  }
}

/**
 * Initialize shell button + terminal modal events.
 * @param {object} deps - { ws: () => WebSocket, activeCwd: () => string, connect: () => void }
 */
export function initShells(deps) {
  wsRef = deps.ws;
  activeCwdRef = deps.activeCwd;
  connectRef = deps.connect;

  if (sbShellsBtn) {
    sbShellsBtn.addEventListener("click", () => {
      haptic("tap");
      const ws = wsRef ? wsRef() : null;
      if (!ws || ws.readyState !== ws.OPEN) {
        connectRef();
      }
      sendShell({ type: "shell_list" });
      openTerminalModal();
    });
  }

  if (terminalNew) {
    terminalNew.addEventListener("click", () => { haptic("tap"); createNewShell(); });
  }

  wireModal({ overlay: terminalOverlay, closeBtn: terminalClose });
  registerEscModal(terminalOverlay, () => closeTerminalModal());
}
